import React, { useEffect, useRef, useState } from 'react';
import { Box, Flex, LoadingIndicator } from '@qga/roo-ui/components';
import GoogleMapReact from 'google-map-react';
import PropTypes from 'prop-types';

const renderMarkers = ({ map, maps }, { lat, lng, title }) => {
  const marker = new maps.Marker({
    position: { lat, lng },
    title,
  });

  marker.setMap(map);
};

const GoogleMap = ({
  apiKey,
  center,
  zoom,
  children,
  pins,
  onGoogleApiLoaded,
  options,
}) => {
  const ref = useRef();
  const [intersected, setIntersected] = useState(false);

  useEffect(() => {
    const el = ref.current;

    if (!el) {
      return () => {};
    }

    const observer = new IntersectionObserver(([{ isIntersecting }]) => {
      if (!isIntersecting) {
        return;
      }

      setIntersected(true);
    });

    observer.observe(el);

    return () => observer.unobserve(el);
  }, []);

  return (
    <Box position="relative" height="100%" data-testid="GOOGLE_MAP" ref={ref}>
      <Flex
        alignItems="center"
        justifyContent="center"
        bg="greys.porcelain"
        position="absolute"
        top="0"
        right="0"
        bottom="0"
        left="0"
      >
        <LoadingIndicator />
      </Flex>
      {intersected && (
        <GoogleMapReact
          bootstrapURLKeys={{ key: apiKey }}
          defaultCenter={center}
          defaultZoom={zoom}
          yesIWantToUseGoogleMapApiInternals
          onGoogleApiLoaded={(googleMap) => {
            pins?.map((pin) => renderMarkers(googleMap, pin));

            if (onGoogleApiLoaded) {
              onGoogleApiLoaded(googleMap);
            }
          }}
          options={{
            streetViewControl: true,
            mapTypeControl: true,
            ...options,
          }}
        >
          {children}
        </GoogleMapReact>
      )}
    </Box>
  );
};

GoogleMap.propTypes = {
  apiKey: PropTypes.string.isRequired,
  children: PropTypes.node,
  center: PropTypes.shape({
    lat: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    lng: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  zoom: PropTypes.number.isRequired,
  pins: PropTypes.arrayOf(
    PropTypes.shape({
      lat: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      lng: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ),
  onGoogleApiLoaded: PropTypes.func,
  options: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

export default GoogleMap;
